<template>
	<div class="col-12 departments-page">
		<div class="card card-outline card-secondary">
			<!-- Encabezado -->
			<div class="card-header">
				<div class="d-flex flex-wrap align-items-center justify-content-between">
					<div>
						<h3 class="card-title mb-0">
							<i class="fa-solid fa-building mr-2"></i>
							Lista de Indivisos
						</h3>
						<br>
						<small class="text-muted d-block mt-1">
							Consulta la disponibilidad.
						</small>
					</div>

					<div class="card-tools mt-2 mt-md-0">
						<button type="button" class="btn btn-sm btn-outline-success" @click="downloadDepartments">
							<i class="fa-solid fa-file-excel mr-1"></i>
							Excel de departamentos
						</button>
					</div>
				</div>
			</div>

			<div class="card-body">
				<!-- Filtros -->
				<div class="filters-panel">
					<div class="d-flex align-items-center justify-content-between mb-3">
						<h6 class="mb-0 font-weight-bold">
							<i class="fa-solid fa-filter mr-1"></i>
							Filtros
						</h6>

						<button v-if="hasActiveFilters" type="button" class="btn btn-sm btn-link text-danger p-0"
							@click="resetFilters">
							<i class="fa-solid fa-xmark mr-1"></i>
							Limpiar filtros
						</button>
					</div>

					<div class="row">
						<div class="col-lg-3 col-md-6 mb-3">
							<label>Empresa</label>

							<select v-model="search.company_id" class="form-control" :disabled="load">
								<option value="">Todas las empresas</option>

								<option v-for="company in catalogue.companies_buildings" :key="company.id"
									:value="company.id">
									{{ company.name }}
								</option>
							</select>
						</div>

						<div class="col-lg-3 col-md-6 mb-3">
							<label>Indiviso</label>

							<select v-model="search.department_id" class="form-control" :disabled="load">
								<option value="">Todos los indivisos</option>

								<option v-for="department in catalogue.departments_all" :key="department.id"
									:value="department.id">
									{{ formatDepartmentOption(department) }}
								</option>
							</select>
						</div>

						<div class="col-lg-3 col-md-6 mb-3">
							<label>Cliente</label>

							<select v-model="search.user_id" class="form-control" :disabled="load">
								<option value="">Todos los clientes</option>

								<option v-for="user in catalogue.users_profile" :key="user.id" :value="user.id">
									{{ getUserName(user) }}
								</option>
							</select>
						</div>

						<div class="col-lg-3 col-md-6 mb-3">
							<label>Estatus</label>

							<select v-model="search.status" class="form-control" :disabled="load">
								<option value="">Todos los estatus</option>
								<option value="1">
									Con contrato activo o próximo
								</option>
								<option value="2">
									Libres
								</option>
								<option value="3">
									Archivados
								</option>
							</select>
						</div>
					</div>
				</div>

				<!-- Resumen -->
				<div class="row summary-section">
					<div class="col-lg-3 col-md-6 mb-3">
						<div class="summary-card summary-total">
							<div class="summary-icon">
								<i class="fa-solid fa-building"></i>
							</div>

							<div>
								<span class="summary-label">Total mostrado</span>
								<strong class="summary-value">
									{{ summary.total }}
								</strong>
							</div>
						</div>
					</div>

					<div class="col-lg-3 col-md-6 mb-3">
						<div class="summary-card summary-active">
							<div class="summary-icon">
								<i class="fa-solid fa-key"></i>
							</div>

							<div>
								<span class="summary-label">Ocupados</span>
								<strong class="summary-value">
									{{ summary.active }}
								</strong>
							</div>
						</div>
					</div>

					<div class="col-lg-3 col-md-6 mb-3">
						<div class="summary-card summary-future">
							<div class="summary-icon">
								<i class="fa-solid fa-calendar-check"></i>
							</div>

							<div>
								<span class="summary-label">Próximos</span>
								<strong class="summary-value">
									{{ summary.future }}
								</strong>
							</div>
						</div>
					</div>

					<div class="col-lg-3 col-md-6 mb-3">
						<div class="summary-card summary-free">
							<div class="summary-icon">
								<i class="fa-solid fa-door-open"></i>
							</div>

							<div>
								<span class="summary-label">Libres</span>
								<strong class="summary-value">
									{{ summary.free }}
								</strong>
							</div>
						</div>
					</div>
				</div>

				<!-- Tabla -->
				<div class="table-container">
					<div class="table-responsive">
						<table class="table table-bordered table-hover text-center text-sm departments-table">
							<thead>
								<tr>
									<th class="column-id">ID</th>
									<th>EDIFICIO</th>
									<th>LOCAL / DEPTO</th>

									<th v-if="canViewContracts" class="column-contract">
										CONTRATO
									</th>

									<th class="column-meters">M²</th>
									<th class="column-status">ESTATUS</th>
									<th>CLIENTE</th>
									<th class="column-actions">OPCIONES</th>
								</tr>
							</thead>

							<tbody>
								<!-- Cargando -->
								<tr v-if="load">
									<td :colspan="tableColspan" class="loading-cell">
										<div class="loading-content">
											<i class="fa-solid fa-circle-notch fa-spin fa-3x mb-3"></i>
											<h5 class="mb-1">Cargando indivisos</h5>
											<small class="text-muted">
												Espera un momento...
											</small>
										</div>
									</td>
								</tr>

								<!-- Error -->
								<tr v-else-if="loadError">
									<td :colspan="tableColspan" class="empty-cell">
										<div class="empty-state text-danger">
											<i class="fa-solid fa-triangle-exclamation fa-3x mb-3"></i>

											<h5>No se pudo cargar la información</h5>

											<p class="mb-3">
												{{ loadError }}
											</p>

											<button type="button" class="btn btn-sm btn-outline-primary" @click="list">
												<i class="fa-solid fa-rotate-right mr-1"></i>
												Volver a intentar
											</button>
										</div>
									</td>
								</tr>

								<!-- Resultados -->
								<template v-else-if="departments.length">
									<tr v-for="department in departments" :key="department.id"
										:class="getRowClass(department)">
										<td class="font-weight-bold">
											{{ department.id }}
										</td>

										<td>
											<div class="building-name">
												<i class="fa-regular fa-building mr-1 text-muted"></i>
												{{ getBuildingName(department) }}
											</div>
										</td>

										<td>
											<div class="department-information">
												<strong>
													{{ department.number || 'Sin número' }}
												</strong>

												<small class="text-muted d-block">
													{{ getDepartmentLevel(department) }}
												</small>
											</div>
										</td>

										<td v-if="canViewContracts">
											<button v-if="hasContract(department)" type="button"
												class="btn btn-sm btn-outline-primary contract-button"
												:title="getContractButtonTitle(department)"
												@click="showContracts(department)">
												<i class="fa-solid fa-file-contract"></i>
											</button>

											<span v-else class="text-muted" title="Sin contrato asignado">
												—
											</span>
										</td>

										<td>
											<span class="meters-value">
												{{ formatSquareMeters(department.square_meter) }}
											</span>
										</td>

										<td>
											<span class="badge status-badge" :class="getStatusBadgeClass(department)">
												<i class="mr-1" :class="getStatusIcon(department)"></i>

												{{ getStatusText(department) }}
											</span>

											<small v-if="getContractState(department) === 'future'"
												class="contract-date d-block mt-1">
												Inicia:
												{{ formatDate(department.contract_active.start_date) }}
											</small>

											<small
												v-if="getContractState(department) === 'active' && department.contract_active"
												class="contract-date d-block mt-1">
												Contrato #{{ department.contract_active.id }}
											</small>
										</td>

										<td>
											<div v-if="getContractUser(department)" class="client-information">
												<div class="client-avatar">
													{{ getClientInitials(department) }}
												</div>

												<div class="client-name">
													{{ getContractUserName(department) }}
												</div>
											</div>

											<span v-else class="text-muted">
												Sin cliente
											</span>
										</td>

										<td>
											<div class="action-buttons">
												<a v-if="department.file" :href="getFileUrl(department)" target="_blank"
													rel="noopener noreferrer" class="btn btn-sm btn-outline-secondary"
													title="Consultar planos">
													<i class="fa-regular fa-file-lines"></i>
													<span class="action-label">Planos</span>
												</a>

												<button v-if="canEditDepartment" type="button"
													class="btn btn-sm btn-outline-primary" title="Editar indiviso"
													@click="editDepartment(department)">
													<i class="fa-solid fa-pen-to-square"></i>
													<span class="action-label">Editar</span>
												</button>

												<span v-if="!department.file && !canEditDepartment" class="text-muted">
													—
												</span>


												<button type="button" class="btn btn-sm ml-1"
												v-if="getContractState(department) === 'free'"
												:class="department.archived_at
													? 'btn-outline-success'
													: 'btn-outline-warning'" :disabled="archiveLoadingId === department.id"
													@click="toggleArchiveDepartment(department)">
													<i class="fas" :class="getArchiveButtonIcon(department)"></i>

													<span class="ml-1">
														{{ getArchiveButtonText(department) }}
													</span>
												</button>


											</div>
										</td>
									</tr>
								</template>

								<!-- Sin resultados -->
								<tr v-else>
									<td :colspan="tableColspan" class="empty-cell">
										<div class="empty-state">
											<i class="fa-solid fa-magnifying-glass fa-3x mb-3"></i>

											<h5>Sin resultados</h5>

											<p class="text-muted mb-3">
												No encontramos indivisos con los filtros seleccionados.
											</p>

											<button v-if="hasActiveFilters" type="button"
												class="btn btn-sm btn-outline-primary" @click="resetFilters">
												<i class="fa-solid fa-filter-circle-xmark mr-1"></i>
												Limpiar filtros
											</button>
										</div>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>

				<!-- Leyenda -->
				<div class="status-legend">
					<span>
						<i class="status-dot dot-active"></i>
						Ocupado
					</span>

					<span>
						<i class="status-dot dot-future"></i>
						Contrato próximo
					</span>

					<span>
						<i class="status-dot dot-free"></i>
						Libre
					</span>
				</div>
			</div>
		</div>

		<ModalContractDepartament />
		<ModalDepartment />
	</div>
</template>

<script>
import ModalContractDepartament from '@/components/contracts/ModalTableContractsAllComponent.vue';
import ModalDepartment from '@/components/departments/ModalFormComponent.vue';

import { mapMutations, mapGetters, mapActions } from 'vuex';
import Catalogue from '@/services/catalogue';
import request from '@/services/request';

export default {
	name: 'DepartmentsTable',

	components: {
		ModalContractDepartament,
		ModalDepartment,
	},

	data() {
		return {
			catalogue: new Catalogue(),
			archiveLoadingId: null,

			load: false,
			loadError: '',
			departments: [],

			searchTimer: null,
			requestSequence: 0,

			search: {
				company_id: '',
				department_id: '',
				user_id: '',
				status: '',
			},
		};
	},

	computed: {
		...mapGetters({
			authUser: 'auth/user',
		}),

		canViewContracts() {
			const invalidProfileIds = [17, 5, 18];
			const profileId = Number(
				this.authUser && this.authUser.organization_charts_id
			);

			return !invalidProfileIds.includes(profileId);
		},

		canEditDepartment() {
			if (!this.authUser) {
				return false;
			}

			const profileId = Number(this.authUser.organization_charts_id);

			if (profileId === 5) {
				return Number(this.authUser.see_departments) === 1;
			}

			return profileId !== 18;
		},

		tableColspan() {
			return this.canViewContracts ? 8 : 7;
		},

		hasActiveFilters() {
			return Object.values(this.search).some(value => {
				return value !== '' && value !== null;
			});
		},

		summary() {
			const result = {
				total: this.departments.length,
				active: 0,
				future: 0,
				free: 0,
			};

			this.departments.forEach(department => {
				const state = this.getContractState(department);

				if (state === 'active') {
					result.active++;
				} else if (state === 'future') {
					result.future++;
				} else {
					result.free++;
				}
			});

			return result;
		},
	},

	mounted() {
		this.setHeaderTitle('Propiedades');

		this.list();
		this.catalogue.get_companies_buildings();
		this.catalogue.get_users_profile(7);
		this.catalogue.get_departments_all();
	},

	created() {
		Event.$on('event-load-buildins', this.list);
		Event.$on('event-load-departaments', this.list);
	},

	beforeDestroy() {
		Event.$off('event-load-buildins', this.list);
		Event.$off('event-load-departaments', this.list);

		if (this.searchTimer) {
			clearTimeout(this.searchTimer);
		}
	},

	methods: {
		...mapActions({
			errorNotification: 'alert/error',
		}),

		...mapMutations({
			setHeaderTitle: 'setHeaderTitle',
		}),

		getArchiveButtonText(department) {
			if (this.archiveLoadingId === department.id) {
				return department.archived_at
					? 'Recuperando'
					: 'Archivando'
			}

			return department.archived_at
				? 'Recuperar'
				: 'Dar de baja'
		},

		getDepartmentName(department) {
			return (
				department.name ||
				department.department ||
				department.description ||
				`Indiviso #${department.id}`
			)
		},

		getArchiveButtonIcon(department) {
			if (this.archiveLoadingId === department.id) {
				return 'fa-spinner fa-spin'
			}

			return department.archived_at
				? 'fa-undo-alt'
				: 'fa-archive'
		},

		async toggleArchiveDepartment(department) {
			const isArchived = Boolean(department.archived_at)

			const swalOptions = {
				icon: isArchived ? 'question' : 'warning',
				title: isArchived
					? 'Recuperar indiviso'
					: 'Dar de baja el indiviso',
				text: isArchived
					? `¿Deseas recuperar ${this.getDepartmentName(department)}? Volverá a aparecer en los catálogos.`
					: `¿Deseas dar de baja ${this.getDepartmentName(department)}? Será desactivado y enviado al archivo muerto.`,
				showCancelButton: true,
				confirmButtonText: isArchived
					? 'Sí, recuperar'
					: 'Desactivar y archivar',
				cancelButtonText: 'Cancelar',
				confirmButtonColor: isArchived
					? '#16a34a'
					: '#d97706',
				reverseButtons: true,
			}

			/*
			 * Solamente solicitamos el motivo cuando se archiva.
			 */
			if (!isArchived) {
				swalOptions.input = 'textarea'
				swalOptions.inputLabel = 'Motivo de la baja'
				swalOptions.inputPlaceholder = 'Escribe el motivo de la baja...'
				swalOptions.inputAttributes = {
					maxlength: 1000,
				}
			}

			const result = await this.$swal(swalOptions)

			if (!result.isConfirmed) {
				return
			}

			this.archiveLoadingId = department.id

			try {
				const endpoint = isArchived
					? `/departments/${department.id}/restore`
					: `/departments/${department.id}/archive`

				const payload = isArchived
					? {}
					: {
						reason: result.value || null,
					}


				const response = await request.post(endpoint, payload)

				this.$swal({
					icon: 'success',
					title: isArchived
						? 'Indiviso recuperado'
						: 'Indiviso archivado',
					text: response.data.message,
					timer: 2500,
					showConfirmButton: false,
				})

				/*
				 * Al volver a consultar:
				 * - Si se archivó, desaparece del listado normal.
				 * - Si se recuperó, desaparece del archivo muerto.
				 */
				await this.list();
			} catch (error) {
				console.error(error)
			} finally {
				this.archiveLoadingId = null
			}
		},

		async list() {
			const currentRequest = ++this.requestSequence;

			this.load = true;
			this.loadError = '';

			const endpoint = '/buildings/departments_used';
			const filters = { ...this.search };

			try {
				const response = await request.post(endpoint, filters);

				/*
				 * Evita que una respuesta anterior reemplace
				 * una búsqueda más reciente.
				 */
				if (currentRequest !== this.requestSequence) {
					return;
				}

				this.departments = Array.isArray(response.data)
					? response.data
					: [];
			} catch (error) {
				if (currentRequest !== this.requestSequence) {
					return;
				}

				this.departments = [];
				this.loadError = 'Revisa tu conexión e intenta nuevamente.';

				if (this.errorNotification) {
					this.errorNotification(error);
				}

				console.error(error);
			} finally {
				if (currentRequest === this.requestSequence) {
					this.load = false;
				}
			}
		},

		scheduleList() {
			if (this.searchTimer) {
				clearTimeout(this.searchTimer);
			}

			this.searchTimer = setTimeout(() => {
				this.list();
			}, 250);
		},

		resetFilters() {
			this.search = {
				company_id: '',
				department_id: '',
				user_id: '',
				status: '',
			};
		},

		downloadDepartments() {
			window.open(
				this.globalUrlFiles + '/buildings/departments',
				'_blank'
			);
		},

		editDepartment(department) {
			Event.$emit('event-show-addDepartament', department);
		},

		showContracts(department) {
			Event.$emit(
				'event-show-ContractsDepartamentAll',
				department
			);
		},

		hasContract(department) {
			return Boolean(
				department &&
				(
					department.contract_active ||
					department.has_contract ||
					Number(department.contracts_valid_count) > 0
				)
			);
		},

		getContractState(department) {
			if (!department || !department.contract_active) {
				return 'free';
			}

			const contract = department.contract_active;
			const startDate = this.parseDate(contract.start_date);
			const endDate = this.parseDate(contract.end_date);
			const today = this.getToday();

			if (startDate && startDate.getTime() > today.getTime()) {
				return 'future';
			}

			const alreadyStarted =
				!startDate ||
				startDate.getTime() <= today.getTime();

			const hasNotEnded =
				!endDate ||
				endDate.getTime() >= today.getTime();

			if (alreadyStarted && hasNotEnded) {
				return 'active';
			}

			return 'free';
		},

		getStatusText(department) {
			const state = this.getContractState(department);

			if (state === 'active') {
				return 'Ocupado';
			}

			if (state === 'future') {
				return 'Contrato próximo';
			}

			return 'Libre';
		},

		getStatusBadgeClass(department) {
			const state = this.getContractState(department);

			if (state === 'active') {
				return 'badge-active';
			}

			if (state === 'future') {
				return 'badge-future';
			}

			return 'badge-free';
		},

		getStatusIcon(department) {
			const state = this.getContractState(department);

			if (state === 'active') {
				return 'fa-solid fa-key';
			}

			if (state === 'future') {
				return 'fa-solid fa-calendar-check';
			}

			return 'fa-solid fa-door-open';
		},

		getRowClass(department) {
			const state = this.getContractState(department);

			return {
				'row-active': state === 'active',
				'row-future': state === 'future',
				'row-free': state === 'free',
			};
		},

		getContractButtonTitle(department) {
			const state = this.getContractState(department);

			if (state === 'future') {
				return 'Consultar contrato próximo';
			}

			return 'Consultar contrato';
		},

		getContractUser(department) {
			if (
				!department ||
				!department.contract_active ||
				!department.contract_active.user
			) {
				return null;
			}

			return department.contract_active.user;
		},

		getContractUserName(department) {
			const user = this.getContractUser(department);

			return user ? this.getUserName(user) : 'Sin cliente';
		},

		getClientInitials(department) {
			const user = this.getContractUser(department);

			if (!user) {
				return '--';
			}

			const firstName = user.name
				? String(user.name).trim().charAt(0)
				: '';

			const lastName = user.last_name
				? String(user.last_name).trim().charAt(0)
				: '';

			return (firstName + lastName).toUpperCase() || '--';
		},

		getUserName(user) {
			if (!user) {
				return '';
			}

			return [
				user.name,
				user.last_name,
			]
				.filter(Boolean)
				.join(' ')
				.trim();
		},

		getBuildingName(department) {
			if (
				department &&
				department.building &&
				department.building.name
			) {
				return department.building.name;
			}

			return 'Sin edificio';
		},

		getDepartmentLevel(department) {
			if (department && department.level) {
				return 'Nivel ' + department.level;
			}

			if (
				department &&
				department.building &&
				department.building.level
			) {
				return 'Nivel ' + department.building.level;
			}

			return 'Nivel no registrado';
		},

		formatDepartmentOption(department) {
			if (!department) {
				return '';
			}

			const building = this.getBuildingName(department);
			const number = department.number || 'Sin número';

			let level = department.level;

			if (
				!level &&
				department.building &&
				department.building.level
			) {
				level = department.building.level;
			}

			return [
				building,
				number,
				level ? 'Nivel ' + level : null,
			]
				.filter(Boolean)
				.join(' - ');
		},

		formatSquareMeters(value) {
			if (
				value === null ||
				value === undefined ||
				value === ''
			) {
				return '—';
			}

			const numericValue = Number(value);

			if (Number.isNaN(numericValue)) {
				return value;
			}

			return numericValue.toLocaleString('es-MX', {
				minimumFractionDigits: 0,
				maximumFractionDigits: 2,
			});
		},

		getFileUrl(department) {
			return this.globalUrlFiles + department.file;
		},

		getToday() {
			const today = new Date();

			return new Date(
				today.getFullYear(),
				today.getMonth(),
				today.getDate()
			);
		},

		parseDate(value) {
			if (
				!value ||
				value === '0000-00-00' ||
				value === '0000-00-00 00:00:00'
			) {
				return null;
			}

			const datePart = String(value).substring(0, 10);
			const parts = datePart.split('-');

			if (parts.length !== 3) {
				return null;
			}

			const year = Number(parts[0]);
			const month = Number(parts[1]);
			const day = Number(parts[2]);

			if (!year || !month || !day) {
				return null;
			}

			return new Date(year, month - 1, day);
		},

		formatDate(value) {
			const date = this.parseDate(value);

			if (!date) {
				return 'Sin fecha';
			}

			return date.toLocaleDateString('es-MX', {
				day: '2-digit',
				month: '2-digit',
				year: 'numeric',
			});
		},
	},

	watch: {
		search: {
			deep: true,
			handler() {
				this.scheduleList();
			},
		},
	},
};
</script>

<style scoped>
.departments-page {
	padding-bottom: 20px;
}

.card-header {
	padding: 16px 20px;
}

.card-title {
	font-size: 18px;
	font-weight: 600;
}

/* Filtros */

.filters-panel {
	padding: 18px 18px 3px;
	margin-bottom: 20px;
	background-color: #f8f9fa;
	border: 1px solid #e3e6e9;
	border-radius: 8px;
}

.filters-panel label {
	margin-bottom: 6px;
	font-size: 13px;
	font-weight: 600;
	color: #495057;
}

.filters-panel .form-control {
	height: 40px;
	font-size: 14px;
	border-radius: 6px;
}

/* Tarjetas de resumen */

.summary-section {
	margin-bottom: 6px;
}

.summary-card {
	display: flex;
	align-items: center;
	min-height: 85px;
	padding: 15px;
	background-color: #ffffff;
	border: 1px solid #e5e7eb;
	border-left: 4px solid #6c757d;
	border-radius: 8px;
	box-shadow: 0 2px 5px rgba(0, 0, 0, 0.04);
}

.summary-icon {
	display: flex;
	align-items: center;
	justify-content: center;
	flex: 0 0 44px;
	width: 44px;
	height: 44px;
	margin-right: 13px;
	font-size: 18px;
	background-color: #f1f3f5;
	border-radius: 50%;
}

.summary-label {
	display: block;
	margin-bottom: 1px;
	font-size: 12px;
	color: #6c757d;
	text-transform: uppercase;
	letter-spacing: 0.3px;
}

.summary-value {
	display: block;
	font-size: 24px;
	line-height: 1.1;
	color: #212529;
}

.summary-total {
	border-left-color: #6c757d;
}

.summary-active {
	border-left-color: #28a745;
}

.summary-future {
	border-left-color: #ffc107;
}

.summary-free {
	border-left-color: #4b72d9;
}

/* Tabla */

.table-container {
	overflow: hidden;
	border: 1px solid #dee2e6;
	border-radius: 8px;
}

.table-responsive {
	max-height: 68vh;
}

.departments-table {
	width: 100%;
	min-width: 1050px;
	margin-bottom: 0;
	table-layout: fixed;
}

.departments-table thead th {
	position: sticky;
	top: 0;
	z-index: 5;
	padding: 12px 8px;
	vertical-align: middle;
	font-size: 12px;
	color: #343a40;
	background-color: #f1f3f5;
	border-bottom: 2px solid #d9dde1;
}

.departments-table tbody td {
	padding: 11px 8px;
	vertical-align: middle;
}

.departments-table tbody tr {
	transition: background-color 0.15s ease;
}

.departments-table tbody tr:hover {
	background-color: #f8fafc !important;
}

.column-id {
	width: 60px;
}

.column-contract {
	width: 90px;
}

.column-meters {
	width: 90px;
}

.column-status {
	width: 175px;
}

.column-actions {
	width: 190px;
}

/* Estado de las filas */

.row-active {
	background-color: #f7faf8;
}

.row-future {
	background-color: #fffdf4;
}

.row-free {
	background-color: #f7f9ff;
}

.row-active td:first-child {
	border-left: 4px solid #28a745;
}

.row-future td:first-child {
	border-left: 4px solid #ffc107;
}

.row-free td:first-child {
	border-left: 4px solid #4b72d9;
}

/* Información */

.building-name {
	font-weight: 500;
}

.department-information strong {
	font-size: 14px;
}

.meters-value {
	font-weight: 600;
	white-space: nowrap;
}

.status-badge {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 105px;
	padding: 6px 9px;
	font-size: 11px;
	font-weight: 600;
	border-radius: 15px;
}

.badge-active {
	color: #155724;
	background-color: #d4edda;
}

.badge-future {
	color: #856404;
	background-color: #fff3cd;
}

.badge-free {
	color: #294b92;
	background-color: #dce6ff;
}

.contract-date {
	font-size: 11px;
	color: #6c757d;
}

.client-information {
	display: flex;
	align-items: center;
	justify-content: center;
	text-align: left;
}

.client-avatar {
	display: flex;
	align-items: center;
	justify-content: center;
	flex: 0 0 32px;
	width: 32px;
	height: 32px;
	margin-right: 8px;
	font-size: 11px;
	font-weight: 700;
	color: #ffffff;
	background-color: #596780;
	border-radius: 50%;
}

.client-name {
	max-width: 170px;
	font-size: 13px;
	font-weight: 500;
	line-height: 1.2;
	word-break: break-word;
}

.contract-button {
	width: 34px;
	height: 32px;
	padding: 4px;
}

/* Acciones */

.action-buttons {
	display: flex;
	flex-wrap: wrap;
	gap: 5px;
	align-items: center;
	justify-content: center;
}

.action-buttons .btn {
	white-space: nowrap;
}

.action-label {
	margin-left: 3px;
}

/* Carga y estados vacíos */

.loading-cell,
.empty-cell {
	height: 250px;
	padding: 0 !important;
}

.loading-content,
.empty-state {
	padding: 50px 20px;
	text-align: center;
}

.loading-content i {
	color: #6c757d;
}

.empty-state i {
	color: #adb5bd;
}

/* Leyenda */

.status-legend {
	display: flex;
	flex-wrap: wrap;
	justify-content: flex-end;
	gap: 18px;
	padding-top: 14px;
	font-size: 12px;
	color: #6c757d;
}

.status-legend span {
	display: inline-flex;
	align-items: center;
}

.status-dot {
	display: inline-block;
	width: 9px;
	height: 9px;
	margin-right: 6px;
	border-radius: 50%;
}

.dot-active {
	background-color: #28a745;
}

.dot-future {
	background-color: #ffc107;
}

.dot-free {
	background-color: #4b72d9;
}

/* Responsive */

@media (max-width: 767px) {
	.card-header {
		padding: 14px;
	}

	.card-body {
		padding: 14px;
	}

	.filters-panel {
		padding: 15px 15px 0;
	}

	.summary-card {
		min-height: 75px;
	}

	.status-legend {
		justify-content: flex-start;
	}

	.action-label {
		display: none;
	}

	.column-actions {
		width: 115px;
	}
}
</style>